(function () {
  'use strict';
  if (window.DDShareCartInstalled || !window.DDShareCartCore) return;
  window.DDShareCartInstalled = true;
  const states = new WeakMap();
  let revision = 0;
  let pendingCartRequests = 0;
  let changedAt = 0;
  const quantitySelector = '.quantity-selector:not(.quantity-selector--product) .quantity-selector__value';
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const roots = () => Array.from(document.querySelectorAll('[data-dd-share-cart]'));
  const codeError = (code) => Object.assign(new Error(code), { code });
  const text = (root, key) => root.querySelector('[data-dd-messages]').dataset[key];
  const status = (root, key) => { root.querySelector('[data-dd-status]').textContent = text(root, key); };

  function invalidate() {
    revision += 1;
    changedAt = Date.now();
    roots().forEach((root) => {
      const state = states.get(root);
      if (!state) return;
      state.url = '';
      root.querySelector('[data-dd-link]').value = '';
      root.querySelector('[data-dd-result]').hidden = true;
      if (!root.querySelector('[data-dd-panel]').hidden) status(root, 'changed');
    });
  }

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      mutation = url.origin === window.location.origin && method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) { mutation = false; }
    if (mutation) { pendingCartRequests += 1; invalidate(); }
    const finish = () => { if (mutation) { pendingCartRequests -= 1; invalidate(); } };
    try {
      const result = originalFetch.apply(this, arguments);
      if (mutation) result.then(finish, finish);
      return result;
    } catch (error) { finish(); throw error; }
  };
  document.addEventListener('cart:rerendered', invalidate, true);
  document.addEventListener('cart:refresh', invalidate, true);
  document.addEventListener('product:added', invalidate, true);
  document.addEventListener('input', (event) => {
    if (event.target.matches(quantitySelector)) invalidate();
  }, true);
  document.addEventListener('change', (event) => {
    if (event.target.matches(quantitySelector)) invalidate();
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-action="increase-quantity"], [data-action="decrease-quantity"], [data-action="remove-item"]')) {
      invalidate();
    }
  }, true);
  window.addEventListener('pageshow', (event) => { if (event.persisted) invalidate(); });

  function unsavedQuantity() {
    return Array.from(document.querySelectorAll(quantitySelector)).some((input) => input.value !== input.defaultValue);
  }

  async function settled() {
    const deadline = Date.now() + 12000;
    while (pendingCartRequests || unsavedQuantity() || Date.now() - changedAt < 350) {
      if (Date.now() > deadline) throw codeError('UPDATING');
      await delay(70);
    }
  }

  async function getJSON(path) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(path, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal
      });
      if (!response.ok) throw codeError(response.status === 404 ? 'UNAVAILABLE_ITEM' : 'NETWORK');
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  async function checkAvailability(cart, rootPath, active) {
    const groups = new Map();
    cart.items.forEach((item) => {
      const handle = item.handle;
      if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) throw codeError('AVAILABILITY');
      if (!groups.has(handle)) groups.set(handle, []);
      groups.get(handle).push(String(item.variant_id));
    });
    const queue = Array.from(groups.entries());
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (next < queue.length) {
        if (!active()) throw codeError('CANCELLED');
        const [handle, ids] = queue[next++];
        const product = await getJSON(rootPath + 'products/' + encodeURIComponent(handle) + '.js');
        if (!Array.isArray(product.variants)) throw codeError('AVAILABILITY');
        for (const id of ids) {
          const variant = product.variants.find((entry) => String(entry.id) === id);
          if (!variant || variant.available !== true) throw codeError('UNAVAILABLE_ITEM');
        }
      }
    }));
  }

  function setBusy(root, busy) {
    root.setAttribute('aria-busy', String(busy));
    root.querySelectorAll('[data-dd-prepare], [data-dd-refresh], [data-dd-copy], [data-dd-native]').forEach((button) => { button.disabled = busy; });
  }

  function stateFor(root) {
    if (!states.has(root)) states.set(root, { busy: false, url: '', version: -1, closed: false, operation: 0 });
    return states.get(root);
  }

  function errorKey(error) {
    if (error.code === 'EMPTY_CART') return 'empty';
    if (error.code === 'UNAVAILABLE_ITEM') return 'unavailable';
    if (error.code === 'UPDATING') return 'updating';
    if (error.code === 'CHANGED') return 'changed';
    if (/^UNSUPPORTED_/.test(error.code || '')) return 'unsupported';
    if (error.code === 'URL_TOO_LONG') return 'tooLong';
    return 'failed';
  }

  async function prepare(root) {
    const state = stateFor(root);
    if (state.busy) return;
    state.busy = true;
    state.closed = false;
    const operation = ++state.operation;
    const active = () => state.operation === operation && !state.closed;
    state.url = '';
    root.querySelector('[data-dd-panel]').hidden = false;
    const panel = root.querySelector('[data-dd-panel]');
    if (typeof panel.showModal === 'function' && !panel.open) panel.showModal();
    root.querySelector('[data-dd-result]').hidden = true;
    root.querySelector('[data-dd-prepare]').setAttribute('aria-expanded', 'true');
    setBusy(root, true);
    status(root, 'preparing');
    try {
      await settled();
      if (!active()) return;
      if (!root.isConnected) return await resume(root, state);
      const version = revision;
      const rootPath = window.Shopify?.routes?.root || '/';
      const options = { origin: root.dataset.origin, root: rootPath };
      window.DDShareCartCore.build({ items: [{ variant_id: 1, quantity: 1 }] }, options);
      const cart = await getJSON(rootPath + 'cart.js');
      if (!active()) return;
      const result = window.DDShareCartCore.build(cart, options);
      await checkAvailability(cart, rootPath, active);
      if (!active()) return;
      const fresh = window.DDShareCartCore.build(await getJSON(rootPath + 'cart.js'), options);
      if (!active()) return;
      if (revision !== version || pendingCartRequests || unsavedQuantity() || fresh.url !== result.url) throw codeError('CHANGED');
      if (!root.isConnected) return await resume(root, state);
      state.url = result.url;
      state.version = version;
      root.querySelector('[data-dd-link]').value = result.url;
      root.querySelector('[data-dd-result]').hidden = false;
      const nativeButton = root.querySelector('[data-dd-native]');
      nativeButton.hidden = typeof navigator.share !== 'function';
      if (!nativeButton.hidden && typeof navigator.canShare === 'function') {
        nativeButton.hidden = !navigator.canShare({ url: result.url });
      }
      status(root, 'ready');
    } catch (error) {
      if (active() && root.isConnected) status(root, errorKey(error));
      else if (active()) await resume(root, state);
    } finally {
      if (state.operation === operation) {
        state.busy = false;
        if (root.isConnected) setBusy(root, false);
      }
    }
  }

  async function resume(root, state) {
    if (state.closed) return;
    const replacement = roots().find((candidate) => candidate.dataset.ddSurface === root.dataset.ddSurface);
    if (replacement && !states.has(replacement)) await prepare(replacement);
  }

  function selectLink(root) {
    const input = root.querySelector('[data-dd-link]');
    input.focus();
    input.select();
    input.setSelectionRange(0, input.value.length);
  }

  async function transfer(root, nativeShare) {
    const state = stateFor(root);
    if (state.busy) return;
    if (!state.url || state.version !== revision || pendingCartRequests || unsavedQuantity()) { status(root, 'changed'); return; }
    const url = state.url;
    const operation = ++state.operation;
    state.busy = true;
    setBusy(root, true);
    try {
      if (nativeShare) {
        await navigator.share({ title: text(root, 'title'), url });
        if (state.operation !== operation) return;
        status(root, state.version === revision ? 'shared' : 'changed');
      } else {
        if (!navigator.clipboard?.writeText) throw codeError('COPY');
        await navigator.clipboard.writeText(url);
        if (state.operation !== operation) return;
        status(root, state.version === revision ? 'copied' : 'changed');
      }
    } catch (error) {
      if (state.operation !== operation) return;
      if (state.version !== revision) status(root, 'changed');
      else if (nativeShare && error.name === 'AbortError') status(root, 'cancelled');
      else { status(root, nativeShare ? 'shareFailed' : 'copyFailed'); selectLink(root); }
    } finally {
      if (state.operation === operation) { state.busy = false; setBusy(root, false); }
    }
  }

  function closePanel(root) {
    const state = stateFor(root);
    state.closed = true;
    state.operation += 1;
    state.busy = false;
    setBusy(root, false);
    const panel = root.querySelector('[data-dd-panel]');
    if (typeof panel.close === 'function') panel.close();
    panel.hidden = true;
    root.querySelector('[data-dd-prepare]').setAttribute('aria-expanded', 'false');
    root.querySelector('[data-dd-prepare]').focus();
  }

  document.addEventListener('cancel', (event) => {
    if (event.target.matches('[data-dd-panel]')) {
      event.preventDefault();
      closePanel(event.target.closest('[data-dd-share-cart]'));
    }
  }, true);

  let sharedEscape = false;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const panel = event.target.closest('[data-dd-panel]');
    if (!panel || panel.hidden) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    sharedEscape = true;
    setTimeout(() => { sharedEscape = false; }, 750);
    closePanel(panel.closest('[data-dd-share-cart]'));
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' && sharedEscape) {
      event.stopImmediatePropagation();
      sharedEscape = false;
    }
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-dd-prepare], [data-dd-refresh], [data-dd-copy], [data-dd-native], [data-dd-close]');
    if (!button) return;
    const root = button.closest('[data-dd-share-cart]');
    if (!root) return;
    event.preventDefault();
    if (button.hasAttribute('data-dd-prepare') || button.hasAttribute('data-dd-refresh')) prepare(root);
    if (button.hasAttribute('data-dd-copy')) transfer(root, false);
    if (button.hasAttribute('data-dd-native')) transfer(root, true);
    if (button.hasAttribute('data-dd-close')) {
      closePanel(root);
    }
  });
  document.documentElement.classList.add('dd-share-cart-ready');
}());
